import { useState } from "react"
import { MoreVertical, Edit, Trash } from "lucide-react"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@f2e/ui/components/base"
import { FormErrorMessage } from "@f2e/ui/components/base-plus"
import { ellipsis } from "@f2e/shared/utils"

import { PermissionMenuItem } from "@/components/Auth"
import { History, Permission, Workspace } from "@/types"

export interface ActionButtonProps {
  className?: string
  history: History
  workspace: Workspace
  onEdit?: (history: History) => void
  onDelete?: (history: History) => void
  sessionScoped?: boolean
}

const editFormSchema = z.object({
  name: z.string().min(1),
})
type EditFormInput = z.infer<typeof editFormSchema>

const ActionButton = ({
  className,
  history,
  workspace,
  onEdit,
  onDelete,
  sessionScoped = false,
}: ActionButtonProps) => {
  const { t } = useTranslation()
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false)

  const editForm = useForm<z.infer<typeof editFormSchema>>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      name: history.name,
    },
  })

  const handleEditConfirm = (formData: EditFormInput) => {
    setShowEditDialog(false)
    editForm.reset()

    const updatedHistory = {
      ...history,
      name: formData.name,
    }
    onEdit?.(updatedHistory)
  }

  return (
    <div className={className} onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="focus-visible:ring-0 focus-visible:ring-offset-0"
            onClick={(event) => event.stopPropagation()}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
          <PermissionMenuItem
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            onClick={() => setShowEditDialog(true)}
          >
            <Edit className="w-4 h-4 mr-2" />
            {t("historyList.card.actions.edit")}
          </PermissionMenuItem>
          <DropdownMenuSeparator />
          <PermissionMenuItem
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash className="w-4 h-4 mr-2" />
            {t("historyList.card.actions.delete")}
          </PermissionMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                sessionScoped
                  ? "historyList.thread.sessionMutation.deleteTitle"
                  : "historyList.card.dialog.delete.title"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                sessionScoped
                  ? "historyList.thread.sessionMutation.deleteDescription"
                  : "historyList.card.dialog.delete.description",
                {
                  name: ellipsis(history.name || "", 54),
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
              {t("historyList.card.dialog.delete.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete?.(history)}>
              {t("historyList.card.dialog.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent onClick={(event) => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>
              {t(
                sessionScoped
                  ? "historyList.thread.sessionMutation.editTitle"
                  : "historyList.card.dialog.edit.title"
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                sessionScoped
                  ? "historyList.thread.sessionMutation.editDescription"
                  : "historyList.card.dialog.edit.description"
              )}
            </DialogDescription>
          </DialogHeader>
          <Form {...editForm}>
            <form
              onSubmit={editForm.handleSubmit(handleEditConfirm)}
              className="space-y-8"
            >
              <FormField
                control={editForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("historyList.card.dialog.edit.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t(
                          "historyList.card.dialog.edit.placeholder"
                        )}
                        {...field}
                      />
                    </FormControl>
                    <FormErrorMessage
                      message={t("historyList.message.nameRequired")}
                    />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit">
                  {t("historyList.card.dialog.edit.save")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export { ActionButton }
export default ActionButton
